import { Badge, DataGrid, type DataGridLeafColumn, Search } from '@ibsys/kit';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DownloadTemplateAttribute } from '@/entities/download-template';
import { dataGridHeightByRows } from '@/shared/lib';

/** Потолок перечня: атрибутов заведён целый справочник, и без него подсказка вытянулась бы на весь их состав */
const ATTRIBUTES_GRID_MAX_HEIGHT = 400;

type Props = {
  /** Атрибуты диалога из справки о переменных: перечень один и тот же у обеих сущностей */
  attributes: DownloadTemplateAttribute[];
};

/** Строка перечня: атрибут вместе с готовой записью, по которой перечень и ищется */
type AttributeRow = DownloadTemplateAttribute & { expression: string };

/**
 * Перечень атрибутов диалога в подсказке: чем атрибут назван в справочнике и как его записывают
 * в файле шаблона. Атрибутов много, поэтому перечень ищется по наименованию и записи — отбор идёт
 * по уже полученной справке, без обращения к серверу.
 */
export const DownloadTemplateAttributesHelp: FC<Props> = ({ attributes }) => {
  const { t } = useTranslation('downloadTemplates');
  const [search, setSearch] = useState('');

  const query = search.trim().toLowerCase();

  const rows = useMemo<AttributeRow[]>(() => {
    const allRows = attributes.map((attribute) => ({
      ...attribute,
      expression: `{{ attributes.${attribute.path} }}`,
    }));

    if (!query) return allRows;

    return allRows.filter(
      (row) => row.name.toLowerCase().includes(query) || row.expression.toLowerCase().includes(query),
    );
  }, [attributes, query]);

  const columns = useMemo<DataGridLeafColumn<AttributeRow>[]>(
    () => [
      {
        id: 'name',
        header: t('attributes.name'),
        size: 320,
        wrap: true,
        cell: ({ row }) => (
          <div className="flex flex-col gap-1">
            <span>{row.name}</span>
            {row.isList && (
              <Badge variant="secondary" outline className="self-start">
                {t('isList')}
              </Badge>
            )}
          </div>
        ),
      },
      {
        id: 'expression',
        header: t('attributes.expression'),
        size: 420,
        wrap: true,
        cell: ({ row }) => <code className="text-sm">{row.expression}</code>,
      },
    ],
    [t],
  );

  return (
    <div className="flex flex-col gap-3">
      <Search
        className="max-w-96"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('attributes.searchPlaceholder')}
      />
      <DataGrid
        className="w-full"
        columns={columns}
        dataSource={{ mode: 'client', data: rows }}
        getRowId={(row) => row.path}
        height={dataGridHeightByRows(rows.length, ATTRIBUTES_GRID_MAX_HEIGHT)}
        emptyState={query ? t('attributes.notFound') : t('attributes.empty')}
        ariaLabel={t('attributes.title')}
      />
    </div>
  );
};
